import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Tags, Sparkles, Layers, Users, ShoppingBag, Settings,
  LogOut, Menu, X, ChevronRight, Truck, ClipboardList, Ticket, Wallet, Image, Heart, Map, UserCog, ArrowLeftRight, FileBarChart, LifeBuoy, Star,
} from 'lucide-react';

import { Logo } from '@/components/brand/Logo';
import { useAuthStore, hasPermission } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * /admin layout — fixed sidebar on the left, content area on the right.
 * Sidebar collapses on mobile behind a hamburger toggle.
 *
 * Branding: ink sidebar + champagne accent + bone content background.
 * Denser typography than the customer site — this is a working surface.
 */
/**
 * Sidebar, grouped by what someone is actually doing rather than by data
 * model. Operations first — that's where a working day is spent.
 *
 * `permission` gates visibility against the backend's own matrix (returned
 * on sign-in), so a supervisor sees the quote queue and nothing else, and a
 * permission change on the server needs no frontend deploy.
 */
interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  exact?: boolean;
  disabled?: boolean;
  permission?: string;
}

const NAV_GROUPS: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Overview',
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    ],
  },
  {
    heading: 'Operations',
    items: [
      // orders.view (not orders.edit) is also held by supervisor and
      // cs_agent -- for context while quoting or handling a customer call,
      // not for the full admin order list. Gating this nav item on
      // orders.edit (admin + ops_manager only) is what actually keeps
      // supervisor's sidebar to Dashboard + Quotes.
      { to: '/admin/orders',    icon: ShoppingBag,   label: 'Orders',    permission: 'orders.edit' },
      { to: '/admin/quotes',    icon: ClipboardList, label: 'Quotes',    permission: 'orders.facility.edit' },
      // drivers.edit, not drivers.view -- supervisor holds drivers.view too
      // (context while quoting), and the Orders/Catalog precedent above is
      // exactly this: gate the nav item on the edit permission or it shows
      // up in a supervisor's sidebar that's supposed to be Dashboard + Quotes only.
      { to: '/admin/drivers',   icon: Truck,         label: 'Drivers',   permission: 'drivers.edit' },
      { to: '/admin/supervisors', icon: UserCog,     label: 'Supervisors', permission: 'supervisors.view' },
      { to: '/admin/handovers', icon: ArrowLeftRight, label: 'Handovers', permission: 'orders.view' },
      { to: '/admin/dispatch',  icon: Map,           label: 'Dispatch',  permission: 'dispatch.edit' },
      { to: '/admin/cashfloats', icon: Wallet,       label: 'Cash',      permission: 'cashfloat.view' },
    ],
  },
  {
    heading: 'Catalog',
    items: [
      // Same reasoning as Orders above: catalog.view is held by supervisor
      // and ops_manager too (for browsing context), so gating the Catalog
      // group on it would put Items/Categories/Services in a supervisor's
      // sidebar. catalog.edit is admin-only.
      { to: '/admin/items',      icon: Package,  label: 'Items',      permission: 'catalog.edit' },
      { to: '/admin/categories', icon: Tags,     label: 'Categories', permission: 'catalog.edit' },
      { to: '/admin/services',   icon: Sparkles, label: 'Services',   permission: 'catalog.edit' },
      { to: '/admin/bundles',    icon: Layers,   label: 'Bundles',    permission: 'catalog.edit' },
    ],
  },
  {
    heading: 'Business',
    items: [
      { to: '/admin/reports',   icon: FileBarChart, label: 'Reports', permission: 'reports.operational.view' },
      { to: '/admin/reviews',  icon: Star,     label: 'Reviews',   permission: 'reviews.view' },
      { to: '/admin/customers', icon: Users,    label: 'Customers', permission: 'customers.view' },
      { to: '/admin/coupons',   icon: Ticket,   label: 'Coupons',   permission: 'coupons.view' },
      { to: '/admin/social-claims', icon: Heart, label: 'Social claims', permission: 'coupons.view' },
      { to: '/admin/flyers',    icon: Image,    label: 'Flyers',    permission: 'coupons.view' },
      { to: '/admin/settings',  icon: Settings, label: 'Settings',  permission: 'settings.operational.edit' },
    ],
  },
  {
    heading: 'Support',
    items: [
      { to: '/admin/help', icon: LifeBuoy, label: 'Help', permission: 'orders.view' },
    ],
  },
];

export function AdminShell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user  = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  function signOut() {
    clear();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-bone flex">
      {/* Mobile toggle */}
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 rounded-xs bg-ink text-bone flex items-center justify-center">
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-40 w-60 bg-ink text-bone flex flex-col transition-transform duration-200',
        'lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="px-5 pt-6 pb-5 border-b border-bone/10">
          <Logo variant="light" compact asLink={false} />
          <p className="mt-2 font-mono text-2xs uppercase tracking-wider text-gold">Admin</p>
        </div>

        <nav className="flex-1 overflow-y-auto py-5">
          {NAV_GROUPS.map((group) => {
            const visible = group.items.filter((i) => !i.permission || hasPermission(user, i.permission));
            if (visible.length === 0) return null;
            return (
              <div key={group.heading} className="mb-4">
                <p className="px-6 mb-1.5 font-body text-2xs uppercase tracking-wider text-bone/35">
                  {group.heading}
                </p>
          <ul className="space-y-0.5 px-3">
            {visible.map((item) => (
              <li key={item.to}>
                {item.disabled ? (
                  <span className="flex items-center gap-3 px-3 py-2 rounded-xs font-body text-sm text-bone/30 cursor-not-allowed">
                    <item.icon size={16} strokeWidth={1.5} />
                    {item.label}
                    <span className="ms-auto font-mono text-2xs uppercase tracking-wider">soon</span>
                  </span>
                ) : (
                  <NavLink to={item.to} end={item.exact}
                           onClick={() => setOpen(false)}
                           className={({ isActive }) => cn(
                             'flex items-center gap-3 px-3 py-2 rounded-xs font-body text-sm transition-colors',
                             isActive
                               ? 'bg-gold/15 text-gold'
                               : 'text-bone/70 hover:text-bone hover:bg-bone/5',
                           )}>
                    <item.icon size={16} strokeWidth={1.5} />
                    {item.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
              </div>
            );
          })}
        </nav>

        <div className="px-5 py-4 border-t border-bone/10">
          <p className="font-display text-sm text-bone leading-tight"
             style={{ fontVariationSettings: "'opsz' 90, 'wght' 500" }}>
            {user?.name || 'Admin'}
          </p>
          <p className="font-mono text-2xs uppercase tracking-wider text-bone/40">{user?.role}</p>
          <button type="button" onClick={signOut}
                  className="mt-3 inline-flex items-center gap-1.5 font-body text-xs text-bone/60 hover:text-bone transition-colors">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {open && (
        <div className="lg:hidden fixed inset-0 bg-ink/40 z-30" onClick={() => setOpen(false)} />
      )}

      {/* Content */}
      <main className="flex-1 lg:ms-60 min-w-0">
        <div className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Page-header pattern used inside admin pages */
export function AdminPageHeader({ title, eyebrow, action }: {
  title: string;
  eyebrow?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-8 pt-6 lg:pt-0 flex items-end justify-between gap-4 flex-wrap">
      <div>
        {eyebrow && <p className="eyebrow text-charcoal/60 mb-2">{eyebrow}</p>}
        <h1 className="font-display text-2xl md:text-3xl text-ink"
            style={{ fontVariationSettings: "'opsz' 144, 'wght' 500" }}>
          {title}
        </h1>
      </div>
      {action}
    </header>
  );
}

/** Crumb breadcrumb pattern for nested admin pages */
export function AdminBreadcrumb({ crumbs }: { crumbs: { label: string; to?: string }[] }) {
  return (
    <nav className="mb-5 flex items-center gap-1.5 font-body text-xs text-charcoal/60">
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {c.to ? (
            <NavLink to={c.to} className="hover:text-ink transition-colors">{c.label}</NavLink>
          ) : (
            <span className="text-ink">{c.label}</span>
          )}
          {i < crumbs.length - 1 && <ChevronRight size={12} className="text-charcoal/30" />}
        </span>
      ))}
    </nav>
  );
}
